import {
  inventorySnapshotFields,
  resolveInventoryAvailability,
} from './store/inventoryAvailability.js'
/**
 * @description 购物车 `/p/shopCart/info/v3` 响应适配：扁平行、汇总金额、合成 shopCarts 供套餐规格弹窗等逻辑使用
 */

import { getOssUrl } from '~/utils'
import { resolveProductModelNo } from '~/utils/tracking/helpers'
import { mergeShopCartMarkingDiscountDto } from '~/utils/cartMarkingDiscountPromo'
import {
  applyLinkedOrderLineAppliedMarkingTags,
  buildCtxForAggregatedMarkingProgress,
  resolveCartLineNextMarkingHintText,
  resolveCartMarkingProgressForLine,
  resolveMarkingDiscountActivityKey,
} from '~/utils/store/discountItemsLineHint'
import { applyOrderComboPromotionPackageDisplay } from '~/utils/store/orderComboPromotionPackageDisplay'
import {
  buildCartComboLineConstraintFields,
  buildComboVOByType3GroupKeyMap,
  resolveComboProdStockCap,
} from '~/utils/store/cartComboLineConstraints'
import {
  isCartComboPackageActivityInvalid,
  isCartLinePromotionParticipating,
  isCartLinePromotionStatusUnavailable,
  resolveCartV3LinePromotionActivityInProgress,
  shouldShowOrderPromotionUi,
  stripOrderLineInactivePromotionDisplay,
} from '~/utils/store/promotionActivityInProgress'
import { resolveBuyGiftPromotionTag } from '~/utils/store/promotionTagResolution'
import {
  giveawayActivityGroupKeyFromRow,
  giveawayProductGroupKeyFromRow,
  isSameGiveawayProductGroup,
} from '~/utils/store/giveawayCartGroupKey'
import {
  mapGiveawayProdToCartGift,
  mapOrderConfirmGiveawayProdsFromLine,
  normalizeGiveawayListForDisplay,
  resolveGiveawayPromotionProdKey,
} from '~/utils/store/giveawayGiftDisplay'
import {
  isSameComboPackageGroup,
  comboPackageGroupKeyFromRow,
  // 兼容旧版函数名
  isSameType3PackageGroup,
  type3PackageGroupKeyFromRow,
} from '~/utils/store/type3PackageGroupKey'
import {
  isComboPackage,
  isComboBundle,
  isComboPromo,
  mapLegacyPromotionType,
  PROMOTION_TYPE,
} from '~/utils/store/promotionTypes'
import { normalizeComboBundleVo } from '~/utils/store/promotionDetail'

/**
 * @param {*} n
 * @returns {number}
 */
function toNum(n) {
  const v = Number(n)
  return Number.isFinite(v) ? v : 0
}

const normalizeBackendTags = (values) => {
  if (!Array.isArray(values)) return []
  return values
    .filter((tag) => typeof tag === 'string')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

/**
 * @description 套餐类活动（搭配套餐4/组合促销5）：同一「活动类型 + promotionCode + comboKey」为一组
 * - 搭配套餐(4)：主行带 comboVO，子行共用主行 comboId
 * - 组合促销(5)：无主商品概念，取第一个 basketId 作为 leader
 * @param {Array<Object>} shopCartItems
 * @returns {{ comboIdByType3GroupKey: Map<string, *>, leaderBasketIdByType3GroupKey: Map<string, *>, comboVOByType3GroupKey: Map<string, Object> }}
 */
export function buildShopCartType3PackageMaps(shopCartItems) {
  const items = Array.isArray(shopCartItems) ? shopCartItems : []
  const comboIdByType3GroupKey = new Map()
  const leaderBasketIdByType3GroupKey = new Map()
  const comboVOByType3GroupKey = buildComboVOByType3GroupKeyMap(items)

  for (const line of items) {
    const pt = Number(line?.promotionType)
    const gk = line?.comboGroupKey
    if (!gk) continue
    const cid = line?.comboVO?.comboId ?? line?.promotionVO?.promotionCode
    if (cid != null && cid !== '' && !comboIdByType3GroupKey.has(gk)) {
      comboIdByType3GroupKey.set(gk, cid)
    }
    // 搭配套餐(4)：TDC comboItemType=1 的主商品作为 leader
    // 组合促销(5)：无主商品概念，第一个作为leader
    if (!leaderBasketIdByType3GroupKey.has(gk)) {
      if (isComboBundle(pt)) {
        if (Number(line?.comboItemType) === 1) {
          leaderBasketIdByType3GroupKey.set(gk, line.basketId)
        }
      } else if (isComboPromo(pt)) {
        leaderBasketIdByType3GroupKey.set(gk, line.basketId) // 第一个
      }
    }
  }

  return { comboIdByType3GroupKey, leaderBasketIdByType3GroupKey, comboVOByType3GroupKey }
}

/**
 * @description 将扁平行按套餐类展示排序：搭配套餐(4)或组合促销(5)
 * - 搭配套餐(4)：主商品置顶，其余按原索引顺序
 * - 组合促销(5)：无主商品概念，按原索引顺序
 * @param {Array<Object>} rows - 已含 promotionType、promotionCode、comboKey、isComboPackageMain、comboItemType 等字段的扁平行
 * @returns {Array<Object>}
 */
export function reorderRowsForType3ComboDisplay(rows) {
  const arr = Array.isArray(rows) ? [...rows] : []
  if (arr.length === 0) return arr

  const comboKeyAt = (idx) => {
    const p = arr[idx]
    return comboPackageGroupKeyFromRow(p) // 使用新函数
  }

  // 搭配套餐(4)：主商品优先
  const rankMain = (p) => {
    if (p?.isComboPackageMain) return 0
    const t = Number(p?.comboItemType)
    if (t === 1) return 0
    return 1
  }

  // 组合促销(5)：无主商品，保持原顺序
  const rankComboPromo = (a, b) => a.idx - b.idx

  const comboStart = new Map()
  for (let i = 0; i < arr.length; i++) {
    const k = comboKeyAt(i)
    if (!k) continue
    comboStart.set(k, Math.min(comboStart.get(k) ?? Infinity, i))
  }

  const membersByCombo = new Map()
  for (let i = 0; i < arr.length; i++) {
    const k = comboKeyAt(i)
    if (!k) continue
    if (!membersByCombo.has(k)) membersByCombo.set(k, [])
    membersByCombo.get(k).push({ idx: i, p: arr[i], pt: Number(arr[i]?.promotionType) })
  }

  // 根据活动类型排序
  for (const list of membersByCombo.values()) {
    const firstPt = list[0]?.pt ?? 4
    if (isComboPromo(firstPt)) {
      // 组合促销(5)：无主商品，保持原顺序
      list.sort(rankComboPromo)
    } else {
      // 搭配套餐(4)：主商品置顶
      list.sort((a, b) => {
        const d = rankMain(a.p) - rankMain(b.p)
        if (d !== 0) return d
        return a.idx - b.idx
      })
    }
  }

  const out = []
  const used = new Set()
  for (let i = 0; i < arr.length; i++) {
    if (used.has(i)) continue
    const k = comboKeyAt(i)
    if (!k) {
      out.push(arr[i])
      continue
    }
    if (comboStart.get(k) !== i) continue
    for (const { idx, p } of membersByCombo.get(k)) {
      used.add(idx)
      out.push(p)
    }
  }
  return out
}

/**
 * @description 将扁平行按分组键聚合并排在组首次出现位置（组内保持原相对顺序）
 * @param {Array<Object>} rows
 * @param {(idx: number, arr: Array<Object>) => string|null|undefined} groupKeyAt
 * @returns {Array<Object>}
 */
function reorderRowsByDisplayGroupKey(rows, groupKeyAt) {
  const arr = Array.isArray(rows) ? [...rows] : []
  if (arr.length === 0 || typeof groupKeyAt !== 'function') return arr

  const groupStart = new Map()
  for (let i = 0; i < arr.length; i++) {
    const k = groupKeyAt(i, arr)
    if (!k) continue
    groupStart.set(k, Math.min(groupStart.get(k) ?? Infinity, i))
  }

  const membersByGroup = new Map()
  for (let i = 0; i < arr.length; i++) {
    const k = groupKeyAt(i, arr)
    if (!k) continue
    if (!membersByGroup.has(k)) membersByGroup.set(k, [])
    membersByGroup.get(k).push({ idx: i, p: arr[i] })
  }

  for (const list of membersByGroup.values()) {
    list.sort((a, b) => a.idx - b.idx)
  }

  const out = []
  const used = new Set()
  for (let i = 0; i < arr.length; i++) {
    if (used.has(i)) continue
    const k = groupKeyAt(i, arr)
    if (!k) {
      out.push(arr[i])
      continue
    }
    if (groupStart.get(k) !== i) continue
    for (const { idx, p } of membersByGroup.get(k)) {
      used.add(idx)
      out.push(p)
    }
  }
  return out
}

/**
 * @description 限时折扣（promotionType=2）同活动同商品多规格聚排
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
export function reorderRowsForMarkingDiscountDisplay(rows) {
  return reorderRowsByDisplayGroupKey(rows, (idx, arr) => {
    const key = resolveMarkingDiscountActivityKey(arr[idx])
    return key || null
  })
}

/**
 * @description 订单行是否套餐主商品（与购物车 `mapV3ShopCartLineToFlatItem` 判定对齐）
 * @param {Record<string, unknown>} item - orderItemDtos 单项
 * @returns {boolean}
 */
function resolveOrderItemIsComboPackageMain(item) {
  return isComboBundle(item?.promotionType) && Number(item?.comboItemType) === 1
}

/**
 * @description 判断订单项是否为赠品行（后端 mergeGiveawayItems 未合并的孤儿）
 * @param {Object} item
 * @returns {boolean}
 * @reason 需与 `isOrderLineGiftItem` 对齐：TDC 常返回 isGift=true + promotionType≠3，漏判会导致售后申请页赠品消失
 */
function isOrderItemGiftLine(item) {
  if (!item || typeof item !== 'object') return false
  const giveawayType = item.giveawayItemType ?? item.giftItemType ?? null
  if (Number(giveawayType) === 2) return true
  if (item.isGiveaway === true || item.isGift === true) return true
  const priceNum = Number(item.price ?? item.actualTotal ?? 0)
  const promoType = Number(item.promotionType ?? 0)
  return giveawayType == null && priceNum === 0 && promoType === 3
}

/**
 * @description 防御性兜底：将孤儿赠品行（后端未能匹配到主商品）挂到前一个非赠品主商品的 giveawayList。
 * 处理历史订单中主商品 promotionType/giveawayItemType 未正确标记的场景。
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
function attachOrphanGiveawayItems(rows) {
  const result = []
  let lastNonGift = null

  for (const row of rows) {
    if (isOrderItemGiftLine(row)) {
      if (lastNonGift) {
        if (!lastNonGift.giveawayList) lastNonGift.giveawayList = []
        lastNonGift.giveawayList.push(row)
        continue
      }
    } else {
      lastNonGift = row
    }
    result.push(row)
  }
  return result
}

/**
 * @description 订单详情/售后 `orderItemDtos` → 商品清单行：套餐主品置顶并标记 `comboOrderSameAsPrev`
 * @param {Array<Object>} orderItemDtos - getOrderDetail 商品列表
 * @param {{ preserveGiveawayList?: boolean }} [options] - 默认 true：保留已落单赠品，不依赖活动状态（与订单详情一致）
 * @returns {Array<Object>}
 */
export function prepareOrderDetailProductRows(orderItemDtos, options = {}) {
  const items = Array.isArray(orderItemDtos) ? orderItemDtos : []
  if (items.length === 0) return []

  let rows = items.map((item) => {
    const comboGroupKey = comboPackageGroupKeyFromRow({
      promotionType: item.promotionType,
      promotionCode: item.promotionCode,
      comboKey: item.comboKey,
    })
    return {
      ...item,
      promotionId: item.promotionCode,
      tags: normalizeBackendTags(item.tags),
      prodId: item.prodId ?? item.productId ?? item.productVO?.prodId,
      comboGroupKey,
      isComboPackageMain: resolveOrderItemIsComboPackageMain(item),
    }
  })

  // 防御性兜底：后端 mergeGiveawayItems 未能匹配的孤儿赠品行，挂到前一个非赠品主商品
  rows = attachOrphanGiveawayItems(rows)

  return finalizeOrderDisplayProductRows(rows, {
    trustApiGiveawayProds: true,
    // 订单详情 / 售后 / 退款：有赠品就保留展示
    preserveGiveawayList: options.preserveGiveawayList !== false,
  })
}

/**
 * @description 订单详情/售后：prepare 后若赠品被清空，回退接口原始 giveawayList（有赠品分组就显示）
 * @param {Array<Object>} preparedRows
 * @param {Array<Object>} rawRows
 * @returns {Array<Object>}
 */
export function mergePreparedRowsWithApiGiveaways(preparedRows, rawRows) {
  const rawList = Array.isArray(rawRows) ? rawRows : []
  return (Array.isArray(preparedRows) ? preparedRows : []).map((item) => {
    const rawItem =
      rawList.find(
        (row) =>
          String(row?.id ?? row?.orderItemId ?? '') === String(item?.id ?? item?.orderItemId ?? '')
      ) || null
    const apiGiveaways = Array.isArray(rawItem?.giveawayList) ? rawItem.giveawayList : []
    const preparedGiveaways = Array.isArray(item.giveawayList) ? item.giveawayList : []
    return {
      ...item,
      giveawayList: preparedGiveaways.length > 0 ? preparedGiveaways : apiGiveaways,
    }
  })
}

/**
 * @description 售后商品清单单行：补齐 ProductTable / MobileProductList 所需展示字段（保留活动标签字段）
 * @param {Object|null|undefined} item - 订单行或售后行
 * @returns {Object|null}
 */
export function mapOrderItemToAfterSalesDisplayRow(item) {
  if (!item || typeof item !== 'object') return null
  const qty = Math.max(0, Number(item.prodCount ?? item.count ?? item.quantity) || 0)
  const lineActual = Number(item.actualTotal ?? item.actual_total ?? NaN)
  const subtotal =
    Number.isFinite(lineActual) && lineActual >= 0
      ? lineActual
      : (Number(item.price) || 0) * (qty > 0 ? qty : 1)
  const pic = item.pic ?? item.image
  return {
    ...item,
    orderItemId: item.orderItemId ?? item.id,
    id: item.orderItemId ?? item.id,
    prodId: item.prodId ?? item.productId,
    prodName: item.prodName ?? item.name,
    name: item.prodName ?? item.name,
    pic,
    image: item.image ?? getOssUrl(pic),
    price: item.price,
    prodCount: qty > 0 ? qty : item.prodCount,
    count: qty > 0 ? qty : item.count,
    quantity: qty > 0 ? qty : item.quantity,
    properties: item.properties ?? item.skuName ?? item.spec ?? '',
    skuName: item.skuName ?? item.properties ?? '',
    giveawayList: Array.isArray(item.giveawayList) ? item.giveawayList : [],
    subtotal,
  }
}

/**
 * @description 售后申请/详情商品列表：与订单详情 `prepareOrderDetailProductRows` 对齐，合并订单行活动字段
 * @param {Object} options
 * @param {number} options.refundType - 1 整单 2 单品/部分
 * @param {Object} [options.refundData] - 含 orderItemDtos（建议已 prepareOrderDetailProductRows）
 * @param {Object} [options.orderDetail] - 订单详情（详情页传入，优先其 orderItemDtos）
 * @param {Array<Object>} [options.refundItems] - 售后单 orderItems，缺省读 refundData.orderItems
 * @returns {Array<Object>}
 * @reason 详情/部分退可能含多行（多主品或赠品独立行）；不可再只取 apiItems[0]
 */
export function resolveAfterSalesDisplayProductRows({
  refundType,
  refundData,
  orderDetail,
  refundItems,
}) {
  const prepared =
    Array.isArray(orderDetail?.orderItemDtos) && orderDetail.orderItemDtos.length > 0
      ? orderDetail.orderItemDtos
      : Array.isArray(refundData?.orderItemDtos)
        ? refundData.orderItemDtos
        : []

  if (Number(refundType) === 1) {
    return prepared.map((row) => mapOrderItemToAfterSalesDisplayRow(row)).filter(Boolean)
  }

  const apiItems = Array.isArray(refundItems)
    ? refundItems
    : Array.isArray(refundData?.orderItems)
      ? refundData.orderItems
      : []

  if (apiItems.length > 0) {
    return apiItems
      .map((fallback) => {
        const orderItemId = fallback.orderItemId ?? fallback.id
        const matched =
          orderItemId != null
            ? prepared.find((row) => String(row.orderItemId ?? row.id) === String(orderItemId))
            : null
        const merged = matched ? { ...matched, ...fallback } : fallback
        return mapOrderItemToAfterSalesDisplayRow(merged)
      })
      .filter(Boolean)
  }

  const fallback = refundData?.prodName ? refundData : null
  if (!fallback) return []

  const orderItemId = fallback.orderItemId ?? fallback.id
  const matched =
    orderItemId != null
      ? prepared.find((row) => String(row.orderItemId ?? row.id) === String(orderItemId))
      : null
  const merged = matched ? { ...matched, ...fallback } : fallback
  const row = mapOrderItemToAfterSalesDisplayRow(merged)
  return row ? [row] : []
}

/**
 * @description 从赠品列表提取名称（订单确认卡片标题等）
 * @param {Array<Object>} gifts
 * @returns {Array<string>}
 */
function extractOrderGiftNameList(gifts = []) {
  if (!Array.isArray(gifts)) return []
  return gifts
    .map((gift) => `${gift?.prodName ?? ''} ${gift?.skuName ?? gift?.properties ?? ''}`.trim())
    .filter(Boolean)
}

/**
 * @description 合并买赠组内各行赠品（优先已有 giveawayList，否则从 giveawayVO 映射）
 * @param {Object} row
 * @param {Map<string, number>} qtyMap
 * @returns {Array<Object>}
 */
function collectGiveawaysForOrderGroupRow(row, qtyMap, options = {}) {
  const fromList = Array.isArray(row?.giveawayList) ? row.giveawayList.filter(Boolean) : []
  // 订单详情：接口已下发的赠品列表直接展示，不依赖活动状态
  if (options.preserveGiveawayList === true && fromList.length > 0) {
    const normalized = normalizeGiveawayListForDisplay(fromList)
    if (normalized.length > 0) return normalized
    // 归一化后为空时仍回退原始列表，避免有赠品分组却被过滤掉
    return fromList.map((gift, index) => ({
      ...gift,
      prodName: gift.prodName ?? gift.productName ?? gift.name ?? `赠品${index + 1}`,
      prodCount: Math.max(
        1,
        Math.trunc(Number(gift.prodCount ?? gift.quantity ?? gift.count ?? gift.giveawayNum) || 1)
      ),
    }))
  }
  if (Number(row?.promotionType) > 0 && !shouldShowOrderPromotionUi(row)) return []
  if (fromList.length > 0) return normalizeGiveawayListForDisplay(fromList)
  if (options.trustApiGiveawayProds) {
    return mapOrderConfirmGiveawayProdsFromLine(row)
  }
  const fromGifts = Array.isArray(row?.gifts) ? row.gifts : []
  if (fromGifts.length > 0 && typeof fromGifts[0] === 'object') return fromGifts
  if (Number(row?.promotionType) !== 3) return []
  const lineForFilter = {
    promotionType: 3,
    promotionId: row.promotionId,
    prodId: row.prodId,
    giveawayVO: row.giveawayVO,
    giveawayItems: row.giveawayItems,
  }
  const fallbackActual = toNum(row.actualTotal ?? row.subtotal ?? row.finalAmount)
  return mapGiveawayProdsToGifts(lineForFilter, fallbackActual, qtyMap)
}

/**
 * @description 订单确认/详情商品行：套餐/限时折扣/买赠同商品多规格聚排、满减标签联动、赠品仅末行规格展示
 * @param {Array<Object>} rows
 * @param {{ qtyByPromotionProdKey?: Map<string, number>, giveawayListKey?: string, giveawayNamesField?: string, trustApiGiveawayProds?: boolean, preserveGiveawayList?: boolean }} [options]
 * @returns {Array<Object>}
 */
export function finalizeOrderDisplayProductRows(rows, options = {}) {
  const listKey = options.giveawayListKey ?? 'giveawayList'
  const namesField = options.giveawayNamesField ?? null
  const trustApiGiveawayProds = options.trustApiGiveawayProds === true
  const preserveGiveawayList = options.preserveGiveawayList === true
  const qtyMap =
    options.qtyByPromotionProdKey instanceof Map
      ? options.qtyByPromotionProdKey
      : buildGiveawayQtyByPromotionProdKey(rows)

  let arr = applyLinkedOrderLineAppliedMarkingTags(
    (Array.isArray(rows) ? rows : []).map((r) => ({ ...r }))
  )
  if (arr.length === 0) return arr

  arr = applyOrderComboPromotionPackageDisplay(arr)

  arr = reorderRowsForType3ComboDisplay(arr)
  for (let i = 0; i < arr.length; i++) {
    const prev = i > 0 ? arr[i - 1] : null
    arr[i].comboOrderSameAsPrev = !!(
      isComboPackage(arr[i].promotionType) &&
      prev &&
      isSameComboPackageGroup(prev, arr[i])
    )
  }

  arr = reorderRowsForMarkingDiscountDisplay(arr)
  for (let i = 0; i < arr.length; i++) {
    const prev = i > 0 ? arr[i - 1] : null
    const key = resolveMarkingDiscountActivityKey(arr[i])
    const prevKey = prev ? resolveMarkingDiscountActivityKey(prev) : ''
    arr[i].sameMarkingDiscountAsPrev = !!(key && prevKey && key === prevKey)
  }

  arr = reorderRowsForGiveawayDisplay(arr)
  arr = applyGiveawayGroupGiftsToFlatLines(arr, qtyMap, {
    listKey,
    namesField,
    trustApiGiveawayProds,
    preserveGiveawayList,
  })

  for (let i = 0; i < arr.length; i++) {
    const prev = i > 0 ? arr[i - 1] : null
    arr[i].sameGiveawayAsPrev = !!(prev && isSameGiveawayProductGroup(prev, arr[i]))
  }

  return arr.map((row) =>
    enrichBuyGiftPromotionDisplayRow(
      stripOrderLineInactivePromotionDisplay(row, { preserveGiveawayList }),
      {
        qtyByPromotionProdKey: qtyMap,
      }
    )
  )
}

/**
 * @description 买赠订单行活动标签：仅展示当前已满足阶梯
 * @param {Object} row
 * @param {{ qtyByPromotionProdKey?: Map<string, number> }} [options]
 * @returns {Object}
 */
function enrichBuyGiftPromotionDisplayRow(row, options = {}) {
  if (Number(row?.promotionType) !== PROMOTION_TYPE.BUY_GIFT) return row
  if (!shouldShowOrderPromotionUi(row)) {
    return { ...row, promotionTag: '', quantityPromotionTag: '' }
  }

  const tag = resolveBuyGiftPromotionTag(row, options)
  return {
    ...row,
    promotionTag: tag,
    quantityPromotionTag: tag,
  }
}

/**
 * @description 限时折扣行按「活动 + 商品」汇总进度，生成「下一档」标签（同商品多规格合并）
 * @param {Array<Object>} flatLines - 扁平行
 * @returns {Map<string, string>} activityKey → markingDiscountTag
 */
/**
 * @description 按目标数量比例缩放折前小计，供改数量草稿期联动满减「下一档」标签
 * @param {Object} line - 扁平行
 * @param {number} nextQty
 * @returns {Object}
 */
export function scaleFlatCartLineQtyForMarkingHint(line, nextQty) {
  const storeQty = Math.max(1, Math.trunc(Number(line?.quantity ?? line?.basketCount) || 1))
  const qty = Math.max(1, Math.trunc(Number(nextQty) || 1))

  let unitOri = Number(line.originalUnitPrice ?? line.originalPrice)
  if (!Number.isFinite(unitOri) || unitOri <= 0) {
    const base = Number(line.baseAmount)
    if (Number.isFinite(base) && base > 0 && storeQty > 0) {
      unitOri = base / storeQty
    }
  }

  const applyQtyAmounts = (row, unitPrice, targetQty) => {
    const lineTotal =
      Number.isFinite(unitPrice) && unitPrice > 0
        ? Math.round(unitPrice * targetQty * 100) / 100
        : null
    if (lineTotal == null) return { ...row, quantity: targetQty, basketCount: targetQty }

    return {
      ...row,
      quantity: targetQty,
      basketCount: targetQty,
      originalUnitPrice: unitPrice,
      baseAmount: lineTotal,
      lineTotalAmount: lineTotal,
      totalAmount: lineTotal,
    }
  }

  if (Number.isFinite(unitOri) && unitOri > 0) {
    return applyQtyAmounts(line, unitOri, qty)
  }

  if (storeQty === qty) return line

  const ratio = qty / storeQty
  const scale = (n) => {
    const v = Number(n)
    return Number.isFinite(v) && v > 0 ? Math.round(v * ratio * 100) / 100 : n
  }

  return {
    ...line,
    quantity: qty,
    basketCount: qty,
    baseAmount: scale(line.baseAmount),
    lineTotalAmount: scale(line.lineTotalAmount),
    totalAmount: scale(line.totalAmount),
    discountAmount: scale(line.discountAmount),
    subtotal: scale(line.subtotal),
    actualTotal: scale(line.actualTotal),
    finalAmount: scale(line.finalAmount),
  }
}

/**
 * @description 扁平行（已勾选）同活动 id 汇总进度 → 下一档满减标签
 * @param {Array<Object>} flatLines
 * @returns {Map<string, string>}
 */
export function buildLinkedMarkingDiscountTagFromFlatLines(flatLines) {
  const items = Array.isArray(flatLines) ? flatLines : []
  const groups = new Map()

  for (const line of items) {
    if (!line || typeof line !== 'object') continue
    if (line.isExpired || line.cartLineStatusInvalid) continue
    if (Number(line.promotionType) === 2 && !isCartLinePromotionParticipating(line)) continue

    const key = resolveMarkingDiscountActivityKey(line)
    if (!key) continue

    if (!groups.has(key)) {
      const mergedDto = mergeShopCartMarkingDiscountDto(line.discountVO, line)
      const itemsForHint = Array.isArray(line.discountVO?.discountItems)
        ? line.discountVO.discountItems
        : Array.isArray(mergedDto?.discountItems)
          ? mergedDto.discountItems
          : []
      const dto =
        itemsForHint.length > 0 ? { ...mergedDto, discountItems: itemsForHint } : mergedDto
      groups.set(key, {
        dto,
        rule: Number(dto?.discountRule),
        lines: [],
      })
    }
    groups.get(key).lines.push(line)
  }

  const tagByKey = new Map()
  for (const [key, group] of groups) {
    let totalProgress = 0
    for (const line of group.lines) {
      if (!line.checked) continue
      totalProgress += resolveCartMarkingProgressForLine(line, group.rule)
    }
    const ctx = buildCtxForAggregatedMarkingProgress(totalProgress, group.rule)
    tagByKey.set(key, resolveCartLineNextMarkingHintText(group.dto, ctx) || '')
  }
  return tagByKey
}

/**
 * @description 扁平行写入 markingDiscountTag（同商品多规格合并计档，normalize 与接口刷新后统一走此口径）
 * @param {Array<Object>} flatLines
 * @returns {Array<Object>}
 */
export function applyLinkedMarkingTagsToFlatCartLines(flatLines) {
  const items = Array.isArray(flatLines) ? flatLines : []
  const tagMap = buildLinkedMarkingDiscountTagFromFlatLines(items)
  return items.map((line) => {
    if (Number(line.promotionType) === 2 && !isCartLinePromotionParticipating(line)) {
      return { ...line, markingDiscountTag: '' }
    }
    const key = resolveMarkingDiscountActivityKey(line)
    const markingDiscountTag =
      key && tagMap.has(key) ? tagMap.get(key) || '' : resolveFlatCartMarkingHintText(line, tagMap)
    return {
      ...line,
      markingDiscountTag: isCartLinePromotionParticipating(line) ? markingDiscountTag : '',
    }
  })
}

/**
 * @description 购物车扁平行满减「下一档」文案（按行分组 Map，否则按单行计算）
 * @param {Object} line - 扁平行（须含 discountVO / promotionType / 金额字段）
 * @param {Map<string, string>} [tagByActivityKey] - buildLinkedMarkingDiscountTagFromFlatLines 结果
 * @returns {string}
 */
export function resolveFlatCartMarkingHintText(line, tagByActivityKey) {
  if (!line || typeof line !== 'object') return ''
  if (Number(line.promotionType) === 2 && !isCartLinePromotionParticipating(line)) return ''

  const key = resolveMarkingDiscountActivityKey(line)
  if (key && tagByActivityKey instanceof Map && tagByActivityKey.has(key)) {
    return tagByActivityKey.get(key) || ''
  }

  if (Number(line.promotionType) !== 2) return ''

  const dto = line.discountVO
  if (!dto || typeof dto !== 'object') {
    return String(line.markingDiscountTag || '').trim()
  }

  const rule = Number(dto.discountRule)
  const progress = resolveCartMarkingProgressForLine(line, rule)
  const ctx = buildCtxForAggregatedMarkingProgress(progress, rule)
  return resolveCartLineNextMarkingHintText(dto, ctx) || ''
}

/**
 * @param {Object} line
 * @param {{ comboIdByType3GroupKey?: Map<string, *>, leaderBasketIdByType3GroupKey?: Map<string, *> }} maps
 * @returns {*|null}
 */
function resolveLineComboIdForSynthetic(line, maps) {
  const direct = line?.comboVO?.comboId
  if (direct != null && direct !== '') return direct
  const gk = type3PackageGroupKeyFromRow(line)
  if (!gk) return null
  const v = maps?.comboIdByType3GroupKey?.get(gk)
  return v != null && v !== '' ? v : null
}

/**
 * @param {Object} raw
 * @returns {{ basketId: number|string, skuId: number|string, prodId: number|string }}
 */
function toDiscountBlockItemRef(raw) {
  return {
    basketId: raw.basketId,
    skuId: raw.skuId,
    prodId: raw.prodId,
  }
}

/**
 * @description 从 V3 行列表合成 shopCarts（单店铺），满足 cart.vue 按 comboId 查找同组 basket 的需求
 * @param {Array<Object>} shopCartItems
 * @returns {Array<Object>}
 */
function buildSyntheticShopCartsFromV3(shopCartItems) {
  const items = Array.isArray(shopCartItems) ? shopCartItems : []
  if (items.length === 0) {
    return [
      {
        shopId: 1,
        stationId: 0,
        shopCartItemDiscounts: [],
      },
    ]
  }

  const type3Maps = buildShopCartType3PackageMaps(items)

  const first = items[0]
  const shopId = toNum(first?.discountVO?.shopId)
  const safeShopId = shopId > 0 ? shopId : 1
  const stationId = toNum(first?.discountVO?.stationId ?? first?.giveawayVO?.stationId)

  const packageGroupToLines = new Map()
  const usedBasket = new Set()

  for (const line of items) {
    const gk = type3PackageGroupKeyFromRow(line)
    const comboId = resolveLineComboIdForSynthetic(line, type3Maps)
    if (gk == null || comboId == null || comboId === '') continue
    if (!packageGroupToLines.has(gk)) packageGroupToLines.set(gk, [])
    packageGroupToLines.get(gk).push(line)
    usedBasket.add(line.basketId)
  }

  const discounts = []

  for (const lines of packageGroupToLines.values()) {
    if (!lines.length) continue
    const head = lines[0]
    const headComboId = resolveLineComboIdForSynthetic(head, type3Maps)
    discounts.push({
      comboGroupKey: type3PackageGroupKeyFromRow(head),
      discountId: head?.discountVO?.discountId,
      chooseComboItemDto:
        headComboId != null && headComboId !== '' ? { comboId: headComboId } : null,
      chooseDiscountItemDto: head.discountVO || null,
      shopCartItems: lines.map(toDiscountBlockItemRef),
    })
  }

  for (const line of items) {
    if (usedBasket.has(line.basketId)) continue
    discounts.push({
      discountId: line?.discountVO?.discountId,
      chooseComboItemDto: null,
      chooseDiscountItemDto: line.discountVO || null,
      shopCartItems: [toDiscountBlockItemRef(line)],
    })
  }

  return [
    {
      shopId: safeShopId,
      stationId,
      shopCartItemDiscounts: discounts,
    },
  ]
}

/**
 * @description 买赠门槛件数：优先单条赠品上的 `buyNum`，否则用 `giveawayVO.buyNum`；均无有效值时返回 null（不拦截，兼容旧数据）
 * @param {Object} line - V3 shopCartItem
 * @param {Object} g - giveawayProds 单项
 * @returns {number|null}
 */
function resolveGiveawayBuyThreshold(line, g) {
  const raw = g?.buyNum ?? line?.giveawayVO?.buyNum
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * @description 当前行主商品件数是否已达该赠品行的买赠门槛（仅本行数量）
 * @param {Object} line - V3 shopCartItem
 * @param {Object} g - giveawayProds 单项
 * @returns {boolean}
 */
function cartLineMeetsGiveawayBuyCondition(line, g) {
  const threshold = resolveGiveawayBuyThreshold(line, g)
  if (threshold == null) return true
  const basketCount = Math.trunc(
    Number(line?.basketCount ?? line?.prodCount ?? line?.quantity ?? 0)
  )
  return Number.isFinite(basketCount) && basketCount >= threshold
}

/**
 * @description 统计购物车/订单内「同活动 + 同主商品」各 SKU 加购件数之和
 * @param {Array<Object>} lines - V3 shopCartItems 或扁平行
 * @returns {Map<string, number>}
 */
export function buildGiveawayQtyByPromotionProdKey(lines) {
  const map = new Map()
  const arr = Array.isArray(lines) ? lines : []
  for (const line of arr) {
    const key = resolveGiveawayPromotionProdKey(line)
    if (!key) continue
    const qty = Math.trunc(Number(line?.basketCount ?? line?.prodCount ?? line?.quantity ?? 0))
    if (!Number.isFinite(qty) || qty < 0) continue
    map.set(key, (map.get(key) || 0) + qty)
  }
  return map
}

/**
 * @description 同活动同主商品多规格合并件数后是否达到买赠门槛
 * @param {Object} line
 * @param {Object} g
 * @param {Map<string, number>} qtyByPromotionProdKey
 * @returns {boolean}
 */
function cartLineMeetsGiveawayBuyConditionAggregated(line, g, qtyByPromotionProdKey) {
  const threshold = resolveGiveawayBuyThreshold(line, g)
  if (threshold == null) return true
  const key = resolveGiveawayPromotionProdKey(line)
  if (!key || !(qtyByPromotionProdKey instanceof Map)) {
    return cartLineMeetsGiveawayBuyCondition(line, g)
  }
  const total = qtyByPromotionProdKey.get(key) ?? 0
  return Number.isFinite(total) && total >= threshold
}

/**
 * @description 按买赠门槛过滤 `giveawayProds`（购物车、订单确认等与 V3 行结构一致处可复用）
 * @param {Object} line - V3 shopCartItem
 * @param {Array<Object>} prods - `giveawayVO.giveawayProds`
 * @param {Object} [options]
 * @param {Map<string, number>} [options.qtyByPromotionProdKey] - 传入时按「同活动同商品」合并件数判断门槛
 * @returns {Array<Object>}
 */
export function filterShopCartGiveawayProdsByBuyCondition(line, prods, options = {}) {
  const arr = Array.isArray(prods) ? prods : []
  const qtyMap = options.qtyByPromotionProdKey
  if (qtyMap instanceof Map) {
    return arr.filter((g) => cartLineMeetsGiveawayBuyConditionAggregated(line, g, qtyMap))
  }
  return arr.filter((g) => cartLineMeetsGiveawayBuyCondition(line, g))
}

/**
 * @description 将 V3 giveawayProds 转为购物车页赠品行结构（与旧扁平结构接近）；未达 `buyNum` 门槛的不输出，避免接口仍带赠品元数据时误展示
 * @param {Object} line - V3 shopCartItem
 * @param {number} fallbackActual
 * @returns {Array<Object>}
 */
function mapGiveawayProdsToGifts(line, fallbackActual, qtyByPromotionProdKey) {
  const tdcItems = Array.isArray(line?.giveawayItems) ? line.giveawayItems : []
  const gv = line?.giveawayVO
  const legacyProds = Array.isArray(gv?.giveawayProds) ? gv.giveawayProds : []
  const prods = tdcItems.length > 0 ? tdcItems : legacyProds
  const filterOpts = qtyByPromotionProdKey instanceof Map ? { qtyByPromotionProdKey } : undefined

  return filterShopCartGiveawayProdsByBuyCondition(line, prods, filterOpts)
    .map((g) =>
      mapGiveawayProdToCartGift(line, g, {
        fallbackActual,
        qtyByPromotionProdKey,
      })
    )
    .filter(Boolean)
}

/**
 * @description V3 购物车行可售库存：仅以 `shopCartItems[].skuVO.stocks` 为准（兼容同对象 `stock` / `skuStock`）
 * @param {Object|null|undefined} line - V3 shopCartItem
 * @returns {number|null} 无法解析时返回 null
 */
export function resolveV3ShopCartLineSkuStock(line) {
  const sv = line?.skuVO || {}
  const inventory = resolveInventoryAvailability(sv)
  if (inventory.hasContract)
    return Number.isFinite(inventory.maxQuantity) ? inventory.maxQuantity : null
  const raw = sv.stocks ?? sv.stock ?? sv.skuStock
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return null
  return n
}

/**
 * @description 起购/最少购买件数（与商品详情套餐逻辑字段对齐）；无效或缺失时视为 1
 * @param {Object} pv - productVO
 * @param {Object} sv - skuVO
 * @param {Object} line - V3 shopCartItem
 * @returns {number}
 */
function resolveMinPurchaseQtyFromCartLineSources(pv, sv, line) {
  const raw =
    pv?.minBuyNum ??
    sv?.minBuyNum ??
    pv?.minBuy ??
    sv?.minBuy ??
    pv?.startSoldNum ??
    sv?.startSoldNum ??
    line?.comboMinNum ??
    pv?.comboMinNum ??
    sv?.comboMinNum ??
    pv?.minNum ??
    sv?.minNum
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * @description V3 行是否可结算：`productVO.prodStatus` 与 `skuVO.status` 均为 1 时有效
 * @param {Object} line - V3 shopCartItem
 * @returns {boolean}
 */
function isV3CartLineProductSkuStatusValid(line) {
  const pv = line?.productVO || {}
  const sv = line?.skuVO || {}
  return toNum(pv.prodStatus) === 1 && toNum(sv.status) === 1
}

/**
 * @description V3 行是否售罄（显式库存快照优先，代理商豁免）
 * @param {Object} line - V3 shopCartItem
 * @returns {boolean}
 */
export function isV3CartLineSoldOut(line, options = {}) {
  return resolveInventoryAvailability(line?.skuVO, {
    ...options,
    legacyQuantity: resolveV3ShopCartLineSkuStock(line),
  }).soldOut
}

/**
 * @description V3 购物车行划线原价（单价）：优先当前 SKU 的 `oriPrice`/`originalPrice`，与 App 一致随规格变化；商品级原价仅作回退
 * @param {Object} line - V3 shopCartItem
 * @param {Object} pv - productVO
 * @param {Object} sv - skuVO
 * @param {number} unitPay - 当前成交单价
 * @param {number} qty - 行数量
 * @returns {number|null}
 */
function resolveV3CartLineOriginalUnitPrice(line, pv, sv, unitPay, qty) {
  const pay = toNum(unitPay)
  const skuOri = toNum(sv.oriPrice ?? sv.originalPrice)
  const salePrice = toNum(sv.price ?? pv.price)
  const pvOriginal = toNum(pv.originalPrice)
  const prodCatalog = toNum(pv.price)

  const catalogUnitCandidates = [skuOri, salePrice, pvOriginal, prodCatalog].filter(
    (v) => v != null && Number.isFinite(v) && v > 0
  )
  const catalogUnit = catalogUnitCandidates.length ? Math.max(...catalogUnitCandidates) : 0
  if (catalogUnit > pay + 0.004) {
    return catalogUnit
  }

  const totalPriceLine = toNum(line.totalPrice)
  const oriFromTotalPrice = qty > 0 && totalPriceLine > 0 ? totalPriceLine / qty : null
  const baseAmt = toNum(line.baseAmount)
  const oriFromBase = qty > 0 && baseAmt > 0 ? baseAmt / qty : null

  const discountItems = line?.discountVO?.discountItems
  const hasMarkingTierDiscount =
    line.discountVO != null &&
    typeof line.discountVO === 'object' &&
    Array.isArray(discountItems) &&
    discountItems.length > 0

  const pt = Number(line.promotionType)
  if (pt === 1 || pt === 3 || hasMarkingTierDiscount) {
    const promoCands = [oriFromTotalPrice, oriFromBase, catalogUnit].filter(
      (v) => v != null && Number.isFinite(v) && v > 0
    )
    if (promoCands.length) {
      const best = Math.max(...promoCands)
      if (best > pay + 0.004) return best
    }
  }

  return null
}

/**
 * @description 单条 V3 shopCartItem → 与 store 旧扁平项对齐的字段；`promotionType=1` 满量特价与 `discountVO.discountItems` 非空的限时满减/档位活动行，会合并 `baseAmount`/`totalPrice`/`productVO.price`/SKU 原价等候选，写入高于成交单价的 `originalUnitPrice` 供订单确认划线展示
 * @param {Object} line
 * @param {number} firstLineActual
 * @param {{ comboIdByType3GroupKey?: Map<string, *>, leaderBasketIdByType3GroupKey?: Map<string, *>, comboVOByType3GroupKey?: Map<string, Object> }} [type3Maps]
 * @param {{ markingDiscountTagOverride?: string }} [options] - 同「活动+商品」分组键时的统一阶梯标签
 * @returns {Object}
 */
export function mapV3ShopCartLineToFlatItem(line, firstLineActual, type3Maps, options = {}) {
  const maps = type3Maps && typeof type3Maps === 'object' ? type3Maps : {}
  const pv = line.productVO || {}
  const sv = line.skuVO || {}
  const rawBasketCount = Math.trunc(
    Number(line.basketCount ?? line.prodCount ?? line.quantity ?? NaN)
  )
  const qty =
    Number.isFinite(rawBasketCount) && rawBasketCount >= 0
      ? rawBasketCount
      : Math.max(1, toNum(line.basketCount ?? line.prodCount ?? 1))

  const finalAmt = toNum(line.finalAmount)
  const activityAmt = toNum(line.activityAmount)
  const baseAmt = toNum(line.baseAmount)

  const unitFromBase = qty > 0 && baseAmt > 0 ? baseAmt / qty : null
  const unitFromActivity = qty > 0 && activityAmt > 0 ? activityAmt / qty : null
  const salePrice = toNum(sv.price ?? pv.price)
  const smb = toNum(sv.smbPrice ?? pv.smbPrice)
  const contractor = toNum(sv.contractorPrice ?? pv.contractorPrice)
  const explicitDisplayPrice = toNum(line.displayUnitPrice ?? line.display_unit_price)

  const price =
    explicitDisplayPrice > 0
      ? explicitDisplayPrice
      : unitFromBase != null && Number.isFinite(unitFromBase)
        ? unitFromBase
        : unitFromActivity != null && Number.isFinite(unitFromActivity)
          ? unitFromActivity
          : salePrice > 0
            ? salePrice
            : smb > 0
              ? smb
              : contractor

  const explicitOriginalUnitPrice = toNum(
    line.displayOriginalUnitPrice ?? line.display_original_unit_price
  )
  const originalUnitPrice =
    explicitOriginalUnitPrice > 0
      ? explicitOriginalUnitPrice
      : resolveV3CartLineOriginalUnitPrice(line, pv, sv, price, qty)

  const shopId = toNum(line.discountVO?.shopId)
  const safeShopId = shopId > 0 ? shopId : 1
  const stationId = toNum(line.discountVO?.stationId ?? line.giveawayVO?.stationId)

  const skuStock = resolveV3ShopCartLineSkuStock(line)
  let hasFiniteStock = skuStock != null
  let effectiveSkuStock = skuStock

  const pt = Number(line.promotionType)

  const mergedMarkingDto = mergeShopCartMarkingDiscountDto(line.discountVO, line)
  const itemsForHint = Array.isArray(line.discountVO?.discountItems)
    ? line.discountVO.discountItems
    : Array.isArray(mergedMarkingDto?.discountItems)
      ? mergedMarkingDto.discountItems
      : []
  const dtoForMarkingHint =
    itemsForHint.length > 0
      ? { ...mergedMarkingDto, discountItems: itemsForHint }
      : mergedMarkingDto

  const lineTotalAmt = toNum(line.totalAmount ?? line.totalPrice ?? 0)
  const lineDiscAmt = toNum(line.discountAmount)
  const markingDiscountTag = Object.prototype.hasOwnProperty.call(
    options,
    'markingDiscountTagOverride'
  )
    ? options.markingDiscountTagOverride
    : resolveCartLineNextMarkingHintText(dtoForMarkingHint, {
        promotionType: pt,
        baseAmount: baseAmt,
        totalAmount: lineTotalAmt,
        originalUnitPrice:
          originalUnitPrice != null && Number.isFinite(originalUnitPrice) && originalUnitPrice > 0
            ? originalUnitPrice
            : undefined,
        discountAmount: lineDiscAmt,
        subtotal: finalAmt > 0 ? finalAmt : activityAmt,
        actualTotal: finalAmt > 0 ? finalAmt : activityAmt,
        finalAmount: finalAmt,
        quantity: qty,
      })

  const ptForGiveaway = Number(line.promotionType)

  const prodStatus = toNum(pv.prodStatus ?? line.prodStatus)
  const status = toNum(line.status ?? prodStatus)

  const groupKey = line.comboGroupKey
  const type3ComboId =
    groupKey && maps.comboIdByType3GroupKey?.has(groupKey)
      ? maps.comboIdByType3GroupKey.get(groupKey)
      : null
  const resolvedComboId =
    line.comboVO?.comboId != null && line.comboVO.comboId !== ''
      ? line.comboVO.comboId
      : type3ComboId != null && type3ComboId !== ''
        ? type3ComboId
        : undefined
  const leaderBasket =
    groupKey && maps.leaderBasketIdByType3GroupKey?.has(groupKey)
      ? maps.leaderBasketIdByType3GroupKey.get(groupKey)
      : undefined

  const fallbackMinPurchaseQty = resolveMinPurchaseQtyFromCartLineSources(pv, sv, line)
  const resolvedPromotionType =
    mapLegacyPromotionType(line.promotionType) ?? Number(line.promotionType)
  const rawComboVO =
    line.comboVO ??
    (groupKey && maps.comboVOByType3GroupKey?.has(groupKey)
      ? maps.comboVOByType3GroupKey.get(groupKey)
      : null)
  const comboVO =
    rawComboVO && isComboBundle(resolvedPromotionType)
      ? normalizeComboBundleVo(rawComboVO)
      : rawComboVO
  const promotionActivityInProgress = resolveCartV3LinePromotionActivityInProgress(line, comboVO)
  const promotionParticipating =
    promotionActivityInProgress &&
    !isCartLinePromotionStatusUnavailable({
      ...line,
      promotionType: line.promotionType,
      promotionId: line.promotionId,
      promotionStatus: line.promotionStatus,
      discountVO: line.discountVO,
      giveawayVO: line.giveawayVO,
      comboVO,
    })

  const nonComboPromotionExpired = pt > 0 && pt !== 3 && !promotionParticipating
  const gifts =
    ptForGiveaway === PROMOTION_TYPE.BUY_GIFT && promotionParticipating
      ? mapGiveawayProdsToGifts(line, firstLineActual, options.qtyByPromotionProdKey)
      : []

  let displayPrice = price
  let displayOriginalUnitPrice = originalUnitPrice
  let displayActualTotal = finalAmt > 0 ? finalAmt : activityAmt

  // 当有活动优惠时（discountAmount > 0），使用优惠价作为显示单价，原价作为划线价
  const discountAmt = toNum(line.discountAmount)
  if (promotionParticipating && discountAmt > 0 && qty > 0 && finalAmt > 0) {
    displayPrice = finalAmt / qty
    displayOriginalUnitPrice = baseAmt > 0 ? baseAmt / qty : displayOriginalUnitPrice
  }

  if (nonComboPromotionExpired) {
    displayPrice = salePrice > 0 ? salePrice : smb > 0 ? smb : contractor
    displayOriginalUnitPrice = resolveV3CartLineOriginalUnitPrice(line, pv, sv, displayPrice, qty)
    displayActualTotal = qty > 0 ? displayPrice * qty : 0
  }

  const stockForComboConstraints = hasFiniteStock ? Math.max(0, effectiveSkuStock) : 999999

  const comboConstraints =
    isComboPackage(line.promotionType) && groupKey && promotionActivityInProgress
      ? buildCartComboLineConstraintFields(
          line,
          comboVO,
          stockForComboConstraints,
          fallbackMinPurchaseQty
        )
      : {
          comboProdMeta: null,
          minPurchaseQty: fallbackMinPurchaseQty,
          comboProductMinPurchaseQty: fallbackMinPurchaseQty,
          comboMaxPurchaseQty: null,
          comboRequired:
            promotionActivityInProgress &&
            (line.comboRequired === true || line.comboRequired === 1 || line.comboRequired === '1'),
          comboDeleteRequiresWholeGroup: false,
        }

  let minPurchaseQty = comboConstraints.minPurchaseQty
  let comboMaxPurchaseQty = comboConstraints.comboMaxPurchaseQty

  if (!sv.inventoryStatus && promotionActivityInProgress && comboConstraints.comboProdMeta) {
    const comboStockCap = resolveComboProdStockCap(comboConstraints.comboProdMeta)
    if (comboStockCap !== null) {
      hasFiniteStock = true
      effectiveSkuStock = comboStockCap
    }
  }

  const stock = hasFiniteStock ? Math.max(0, effectiveSkuStock) : 999999
  const inventory = resolveInventoryAvailability(sv, {
    isAgencyUser: options.isAgencyUser,
    legacyQuantity: hasFiniteStock ? effectiveSkuStock : null,
  })
  const isSoldOut = inventory.soldOut && isV3CartLineProductSkuStatusValid(line)

  const cartLineStatusInvalid = !isV3CartLineProductSkuStatusValid(line)
  const comboPackageInvalid =
    isComboBundle(resolvedPromotionType) &&
    groupKey != null &&
    groupKey !== '' &&
    isCartComboPackageActivityInvalid({
      promotionType: line.promotionType,
      promotionId: line.promotionId,
      promotionStatus: line.promotionStatus,
      comboGroupKey: groupKey,
      comboVO,
      discountVO: line.discountVO,
    })
  const checked =
    !cartLineStatusInvalid &&
    !inventory.blocked &&
    !comboPackageInvalid &&
    (line.isChecked === 1 || line.isChecked === true)

  const buyGiftPromotionTag =
    promotionParticipating && ptForGiveaway === PROMOTION_TYPE.BUY_GIFT
      ? resolveBuyGiftPromotionTag(
          {
            promotionType: line.promotionType,
            promotionId: line.promotionId,
            prodId: line.prodId ?? pv.prodId,
            promotionHierarchies: line.promotionHierarchies,
            giveawayVO: line.giveawayVO,
            quantity: qty,
          },
          { qtyByPromotionProdKey: options.qtyByPromotionProdKey }
        )
      : ''

  return {
    id: line.basketId,
    basketId: line.basketId,
    skuId: line.skuId ?? sv.skuId,
    prodId: line.prodId ?? pv.prodId,
    prodUnit: pv.prodUnit ?? line.prodUnit ?? '',
    shopId: safeShopId,
    stationId,
    dvyType: line.dvyType ?? 1,
    modelNo: resolveProductModelNo(pv) || resolveProductModelNo(line) || '',
    name: pv.prodName ?? '',
    prodName: pv.prodName ?? '',
    originalUnitPrice: displayOriginalUnitPrice,
    price: displayPrice,
    actualTotal: displayActualTotal,
    image: getOssUrl(sv.pic || pv.pic),
    tags: normalizeBackendTags(line.tags),
    spec: sv.skuName ?? '',
    ...inventorySnapshotFields(sv),
    inventoryExempt: options.isAgencyUser === true,
    stock,
    stocks: hasFiniteStock ? effectiveSkuStock : undefined,
    isSoldOut,
    quantity: qty,
    baseAmount: baseAmt,
    lineTotalAmount: lineTotalAmt,
    promotionTag: buyGiftPromotionTag,
    markingDiscountTag: promotionParticipating ? markingDiscountTag : '',
    unitPromotionTag: promotionParticipating
      ? String(
          line.unitPromotionTag ??
            line.appliedPromotionTag ??
            line.enjoyedPromotionTag ??
            line.appliedDiscountTag ??
            ''
        ).trim()
      : '',
    quantityPromotionTag:
      promotionParticipating && ptForGiveaway === PROMOTION_TYPE.BUY_GIFT
        ? buyGiftPromotionTag
        : promotionParticipating
          ? String(line.quantityPromotionTag ?? line.nextPromotionTag ?? '').trim()
          : '',
    gifts,
    checked,
    cartLineStatusInvalid,
    comboId: resolvedComboId,
    comboGroupKey: groupKey,
    comboKey: line.comboKey,
    comboItemType: line.comboItemType,
    comboRequired: comboConstraints.comboRequired,
    comboStartNum:
      line.comboStartNum ?? line.combo_start_num ?? comboConstraints.comboProductMinPurchaseQty,
    comboMaxNum: line.comboMaxNum ?? line.combo_max_num ?? comboConstraints.comboMaxPurchaseQty,
    comboProdMeta: comboConstraints.comboProdMeta,
    comboMaxPurchaseQty,
    comboDeleteRequiresWholeGroup: comboConstraints.comboDeleteRequiresWholeGroup,
    comboGroupLeaderBasketId: leaderBasket,
    isComboPackageMain: isComboBundle(resolvedPromotionType) && Number(line.comboItemType) === 1,
    comboVO: comboVO && typeof comboVO === 'object' ? comboVO : undefined,
    discountId: line.discountVO?.discountId,
    discountVO: dtoForMarkingHint,
    discountAmount: toNum(line.discountAmount),
    couponIds: Array.isArray(line.couponIds) ? line.couponIds : [],
    couponReduceAmount: toNum(line.couponReduceAmount),
    transFee: toNum(line.transFee),
    status,
    isDelisted: line.isDelisted ?? pv.isDelisted,
    isExpired: false,
    promotionType: line.promotionType,
    promotionCode: line.promotionCode,
    promotionId: line.promotionCode,
    promotionName: line.promotionName,
    promotionStatus: line.promotionStatus,
    promotionActivityInProgress,
    promotionParticipating,
    promotionHierarchies: Array.isArray(line.promotionHierarchies)
      ? line.promotionHierarchies
      : null,
    totalProdQuantity: line.totalProdQuantity ?? null,
    minPurchaseQty,
    giveawayVO:
      line.giveawayVO && typeof line.giveawayVO === 'object' ? line.giveawayVO : undefined,
    giveawayGroupKey: giveawayActivityGroupKeyFromRow(line) ?? undefined,
    giveawayProductGroupKey: giveawayProductGroupKeyFromRow(line) ?? undefined,
    markingDiscountGroupKey: resolveMarkingDiscountActivityKey(line) || undefined,
  }
}

/**
 * @description 买赠行按「活动 + 主商品 prodId」聚合排序（同商品多规格相邻）
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
export function reorderRowsForGiveawayDisplay(rows) {
  return reorderRowsByDisplayGroupKey(rows, (idx, arr) => giveawayProductGroupKeyFromRow(arr[idx]))
}

/**
 * @description 买赠「同活动 + 同主商品」组内合并凑单赠品，仅在该商品最后一行规格下展示赠品列表
 * @param {Array<Object>} rows - 已排序的扁平行
 * @param {Map<string, number>} qtyByPromotionProdKey
 * @param {{ listKey?: string, namesField?: string }} [options] - 购物车默认 `gifts`；订单确认/详情用 `giveawayList`
 * @returns {Array<Object>}
 */
export function applyGiveawayGroupGiftsToFlatLines(rows, qtyByPromotionProdKey, options = {}) {
  const arr = Array.isArray(rows) ? rows.map((r) => ({ ...r })) : []
  if (arr.length === 0) return arr
  const qtyMap = qtyByPromotionProdKey instanceof Map ? qtyByPromotionProdKey : new Map()
  const listKey = options.listKey ?? 'gifts'
  const namesField = options.namesField ?? null
  const trustApiGiveawayProds = options.trustApiGiveawayProds === true
  const preserveGiveawayList = options.preserveGiveawayList === true

  const indicesByGroup = new Map()
  for (let i = 0; i < arr.length; i++) {
    const k = giveawayProductGroupKeyFromRow(arr[i])
    if (!k) continue
    if (!indicesByGroup.has(k)) indicesByGroup.set(k, [])
    indicesByGroup.get(k).push(i)
  }

  for (const indices of indicesByGroup.values()) {
    if (indices.length === 0) continue

    const leaderId = arr[indices[0]].id ?? arr[indices[0]].basketId ?? arr[indices[0]].orderItemId
    const mergedGifts = []
    const seenGiftKey = new Set()

    for (const idx of indices) {
      const row = arr[idx]
      if (
        !preserveGiveawayList &&
        Number(row.promotionType) > 0 &&
        !shouldShowOrderPromotionUi(row)
      ) {
        continue
      }
      if (
        !trustApiGiveawayProds &&
        Number(row.promotionType) === PROMOTION_TYPE.BUY_GIFT &&
        !isCartLinePromotionParticipating(row)
      ) {
        continue
      }
      const mapped = collectGiveawaysForOrderGroupRow(row, qtyMap, {
        trustApiGiveawayProds,
        preserveGiveawayList,
      })
      for (const gift of mapped) {
        const gk = `${gift.prodId ?? ''}_${gift.skuId ?? ''}_${gift.id ?? gift.orderItemId ?? gift.prodName ?? ''}`
        if (seenGiftKey.has(gk)) continue
        seenGiftKey.add(gk)
        mergedGifts.push(gift)
      }
    }

    const lastIdx = indices[indices.length - 1]
    indices.forEach((idx, pos) => {
      const originalList = Array.isArray(arr[idx].giveawayList)
        ? arr[idx].giveawayList.filter(Boolean)
        : []
      // 订单详情保留接口赠品：合并结果为空时不要覆盖成 []
      const nextList =
        idx === lastIdx
          ? mergedGifts.length > 0
            ? mergedGifts
            : preserveGiveawayList
              ? originalList
              : []
          : []
      const patch = {
        [listKey]: nextList,
        giveawayGroupLeaderId: leaderId,
        sameGiveawayAsPrev: pos > 0,
        isGiveawayGroupGiftHolder: idx === lastIdx && nextList.length > 0,
      }
      if (namesField) {
        patch[namesField] = idx === lastIdx ? extractOrderGiftNameList(nextList) : []
      }
      arr[idx] = { ...arr[idx], ...patch }
    })
  }

  return arr
}

/**
 * @description 购物车 V3 响应是否含已过期活动（后端已自动刷新行上活动维度）
 * @param {Object|null|undefined} payload - `/p/shopCart/info/v3` 内层 data
 * @returns {boolean}
 */
export function isCartHasExpiredPromotion(payload) {
  if (!payload || typeof payload !== 'object') return false
  const raw = payload.hasExpiredPromotion ?? payload.has_expired_promotion
  return raw === true || raw === 1 || raw === '1'
}

/**
 * @description 将 V3 data 归一化为与现有 getters / cart.vue 兼容的 cartData，并生成 flatItems、selectedIds
 * @param {Object} res - 接口内层 data（已解包）
 * @returns {{ cartData: Object, flatItems: Array<Object>, selectedIds: Array }}
 */
export function normalizeShopCartInfoV3(res, options = {}) {
  const payload = res && typeof res === 'object' ? res : {}
  const items = Array.isArray(payload.shopCartItems) ? payload.shopCartItems : []
  const firstLineActual =
    items[0]?.finalAmount ??
    items[0]?.activityAmount ??
    payload.totalAmount ??
    payload.totalMoney ??
    0

  const type3Maps = buildShopCartType3PackageMaps(items)
  const giveawayQtyByPromotionProdKey = buildGiveawayQtyByPromotionProdKey(items)
  const flatItemsRaw = items.map((line) =>
    mapV3ShopCartLineToFlatItem(line, toNum(firstLineActual), type3Maps, {
      qtyByPromotionProdKey: giveawayQtyByPromotionProdKey,
      isAgencyUser: options.isAgencyUser,
    })
  )
  const flatItemsTagged = applyLinkedMarkingTagsToFlatCartLines(flatItemsRaw)
  const flatComboOrdered = reorderRowsForType3ComboDisplay(flatItemsTagged)
  const flatMarkingOrdered = reorderRowsForMarkingDiscountDisplay(flatComboOrdered)
  const flatGiveawayOrdered = reorderRowsForGiveawayDisplay(flatMarkingOrdered)
  const flatItems = applyGiveawayGroupGiftsToFlatLines(
    flatGiveawayOrdered,
    giveawayQtyByPromotionProdKey,
    {
      listKey: 'gifts',
    }
  )

  const selectedIds = []
  for (const it of flatItems) {
    if (
      it.checked &&
      !it.isExpired &&
      !it.cartLineStatusInvalid &&
      !isCartComboPackageActivityInvalid(it)
    ) {
      selectedIds.push(it.id)
    }
  }

  const totalAmount = toNum(payload.totalAmount)
  const totalDiscount = toNum(payload.totalDiscountAmount)
  const transFee = toNum(payload.totalTransFee)
  const totalFinalAmount = toNum(payload.totalFinalAmount)
  const coupons = Array.isArray(payload.coupons) ? payload.coupons : []
  const couponReduceTotal = items.reduce((acc, line) => acc + toNum(line?.couponReduceAmount), 0)

  const cartData = {
    ...payload,
    coupons,
    totalMoney: totalAmount,
    totalReduceMoney: totalDiscount,
    freightAmount: transFee,
    freeShippingThreshold: payload.freeShippingThreshold,
    freightMatched: payload.freightMatched === true,
    isFreeShipping: payload.isFreeShipping === true,
    freightTip: payload.freightTip || null,
    amountToFreeShipping: payload.amountToFreeShipping ?? null,
    couponReduceAmount: couponReduceTotal,
    finalMoney: totalFinalAmount,
    shopCarts: buildSyntheticShopCartsFromV3(items),
  }

  return { cartData, flatItems, selectedIds }
}
